import { Head, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Collapsible } from "@/components/ui/collapsible"
import { DataTable, DataRow } from "@/components/ui/data-table"
import { NavTabs, type NavTab } from "@/components/ui/nav-tabs"
import { AUDIT_TABS, QUESTIONS_PATH } from "@/components/admin/audits"
import {
  INTAKE_PRODUCTS,
  findProduct,
  questionCount,
  stageCount,
  type IntakeProduct,
  type IntakeQuestion,
  type IntakeStage,
} from "@/components/admin/intake-questions"

const DESCRIPTION =
  "What each audit type asks a client, in the order they are asked, with the helper text shown alongside each question."

// One tab per deck kind, distinguished by a query param so each is linkable and
// survives a refresh. The first is the default, so it matches a bare path too.
const PRODUCT_TABS: NavTab[] = INTAKE_PRODUCTS.map((product, index) => ({
  href: `${QUESTIONS_PATH}?type=${product.key}`,
  label: product.name,
  match: (url) => {
    const type = url.split("?")[1]?.match(/(?:^|&)type=([^&]*)/)?.[1]
    return type ? type === product.key : index === 0
  },
}))

function QuestionBody({ question }: { question: IntakeQuestion }) {
  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="muted">{question.type}</Badge>
        {question.required && <Badge tone="accent">Required to submit</Badge>}
      </div>
      {question.helper && <p className="mt-2 text-sm">{question.helper}</p>}
      {question.options && (
        <p className="mt-2 text-sm text-ink-muted">
          Options: {question.options.join(" · ")}
        </p>
      )}
    </div>
  )
}

function Stage({ stage, multiple }: { stage: IntakeStage; multiple: boolean }) {
  return (
    <section className={multiple ? "mt-10 border-l-2 border-hairline pl-5" : "mt-8"}>
      {stage.title && (
        <>
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3>{stage.title}</h3>
            <Badge tone="muted">{stageCount(stage)} questions</Badge>
          </div>
          {stage.note && <p className="mt-1 max-w-3xl text-sm text-ink-muted">{stage.note}</p>}
        </>
      )}

      {stage.steps.map((step, index) => (
        <div key={`${stage.title ?? ""}-${step.title}`} className="mt-6">
          <h4>
            {index + 1}. {step.title}
          </h4>
          {step.note && <p className="mt-1 max-w-3xl text-sm text-ink-muted">{step.note}</p>}
          <DataTable className="mt-3">
            {step.questions.map((question) => (
              <DataRow key={question.field} title={question.label}>
                <QuestionBody question={question} />
              </DataRow>
            ))}
          </DataTable>
        </div>
      ))}
    </section>
  )
}

function Product({ product }: { product: IntakeProduct }) {
  const multiple = product.stages.length > 1

  return (
    <div className="mt-8">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h2>{product.name}</h2>
        <Badge tone="accent">{questionCount(product)} questions</Badge>
        <Badge tone="muted">{product.form}</Badge>
      </div>
      <p className="mt-2 max-w-3xl">{product.summary}</p>

      {product.stages.map((stage) => (
        <Stage key={stage.title ?? "only"} stage={stage} multiple={multiple} />
      ))}

      <div className="mt-10">
        <Collapsible
          summary={
            <span className="text-sm text-ink-display">
              Internal tracker fields ({product.internal.length}) — not asked of the client
            </span>
          }
        >
          <p className="mb-3 max-w-3xl text-sm text-ink-muted">
            Filled in as the audit runs, not by the client. Listed here so the
            whole record is visible in one place.
          </p>
          <DataTable>
            {product.internal.map((field) => (
              <DataRow key={field.field} title={field.label}>
                <QuestionBody question={field} />
              </DataRow>
            ))}
          </DataTable>
        </Collapsible>
      </div>
    </div>
  )
}

export default function AdminAuditQuestions() {
  const { url } = usePage()
  const type = new URLSearchParams(url.split("?")[1] ?? "").get("type")
  const product = findProduct(type)

  return (
    <>
      <Head title={`Intake questions — ${product.name}`}>
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Intake questions" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AdminShell wide>
        <PageHeader
          title="Audits"
          description="What each audit type asks, and what gets tracked internally"
        />
        <NavTabs tabs={AUDIT_TABS} current={url} label="Audits" className="mt-6" />

        <p className="mt-6 max-w-3xl text-sm text-ink-muted">
          Reference only — nothing on this page captures an answer. There are two
          questionnaires behind these three decks: combined is the with-access
          form plus a top-up, not a third form.
        </p>

        <NavTabs
          tabs={PRODUCT_TABS}
          current={url}
          label="Audit type"
          className="mt-4 border-b-0"
        />

        <Product product={product} />
      </AdminShell>
    </>
  )
}
